import { Fragment } from "react";
import { Css } from "~generated/css";
import { formatAmount, formatDeltaPriceDisplay, isDefined, useTestIds } from "~utils";

type PrintListItemProps = {
  title?: string;
  priceInCents: number;
  items: {
    title: string;
    priceInCents?: number;
    optionCode?: string;
  }[];
  shouldDisplayPricing: boolean;
  // Indent options after the first for sub items
  shouldIndent?: boolean;
  // Customer-facing reports hide BP options and Opt codes
  hideOptionCodes?: boolean;
};

export const PrintListItem = (props: PrintListItemProps) => {
  const { title, priceInCents, items, shouldDisplayPricing, shouldIndent = false, hideOptionCodes = false } = props;
  const tid = useTestIds(props, "print-list-item");

  // If all sub items are included, we just want the main title item to show "Included"
  const allSubItemsIncluded = items.every((item) => !isDefined(item.priceInCents) || item.priceInCents === 0);

  return (
    <div css={Css.pyPx(12).$}>
      {title && (
        <div css={Css.df.w100.aic.jcsb.$}>
          {/* 56% is sweetspot for line break */}
          <div css={Css.dif.w("56%").$}>
            <div css={Css.t14.fw500.lhPx(22).$} {...tid.groupTitle}>
              {title}
            </div>
          </div>
          {shouldDisplayPricing && (
            <div css={Css.df.gap("12px").$}>
              <span css={{ ...Css.t14.lhPx(24).gray700.wsnw.$, ...Css.gray800.fw500.$ }} {...tid.groupPriceInCents}>
                {priceInCents === 0 ? "Included" : formatAmount(priceInCents)}
              </span>
            </div>
          )}
        </div>
      )}
      {items.map((item, idx) => {
        const { optionCode, title, priceInCents } = item;
        // remove the required asterisk from the name
        const formattedTitle = title.replaceAll("*", "");
        const indentRow = idx > 0 && shouldIndent;
        return (
          <Fragment key={formattedTitle}>
            <div
              css={Css.df.w("95%").mxa.aife.jcsb.mt1.add("borderBottom", "dotted").add("borderBottomWidth", "thin").$}
            >
              {/* 56% is sweetspot for line break */}
              <div css={Css.dif.w("56%").$}>
                <div css={Css.t12.gray700.wspl.if(indentRow).pl2.$} {...tid.itemTitle}>
                  {optionCode && !hideOptionCodes && `[${optionCode}] `}
                  {formattedTitle}
                </div>
              </div>
              {shouldDisplayPricing && isDefined(priceInCents) && !allSubItemsIncluded && (
                <div css={Css.df.gap("12px").$}>
                  <span css={Css.t12.gray700.wsnw.$} {...tid.itemPriceInCents}>
                    {formatDeltaPriceDisplay(priceInCents)}
                  </span>
                </div>
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};
